import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Users, Layers, Settings, Shield } from "lucide-react";

const ROLES = ["admin", "doctor", "receptionist", "lab_technician", "staff", "patient"];
const ENTITIES = ["doctor", "patient", "staff", "vendor", "appointment"];
const FIELD_TYPES = ["text", "number", "date", "select", "textarea"];

export default function SettingsPage() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "staff", phone: "" });
  const [fieldForm, setFieldForm] = useState({ entity: "patient", field_name: "", field_type: "text", options: "", required: false });
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [u, f] = await Promise.all([api.get("/users"), api.get("/settings/fields")]);
      setUsers(u.data); setFields(f.data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveUser = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, userForm);
        toast({ title: "User updated" });
      } else {
        await api.post("/users", userForm);
        toast({ title: "User created" });
      }
      setShowUserModal(false);
      setUserForm({ name: "", email: "", password: "", role: "staff", phone: "" });
      setEditUser(null);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast({ title: "User deleted" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const handleToggleUser = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const handleSaveField = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...fieldForm,
        options: fieldForm.field_type === "select" && fieldForm.options ? fieldForm.options.split(",").map(s => s.trim()) : null,
      };
      await api.post("/settings/fields", payload);
      toast({ title: "Custom field added" });
      setShowFieldModal(false);
      setFieldForm({ entity: "patient", field_name: "", field_type: "text", options: "", required: false });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDeleteField = async (id) => {
    if (!window.confirm("Delete this field?")) return;
    try {
      await api.delete(`/settings/fields/${id}`);
      toast({ title: "Field deleted" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const roleColors = {
    admin: "bg-rose-100 text-rose-800",
    doctor: "bg-emerald-100 text-emerald-800",
    receptionist: "bg-blue-100 text-blue-800",
    lab_technician: "bg-violet-100 text-violet-800",
    staff: "bg-slate-100 text-slate-700",
    patient: "bg-amber-100 text-amber-800",
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("settings")}</h1>
        <p className="text-slate-500 text-sm mt-1">Manage users, access control, and custom fields</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="users" className="gap-2"><Users size={14} /> User Management</TabsTrigger>
          <TabsTrigger value="fields" className="gap-2"><Layers size={14} /> Custom Fields</TabsTrigger>
          <TabsTrigger value="roles" className="gap-2"><Shield size={14} /> Roles Info</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-600 font-medium">{users.length} total users</p>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" size="sm" onClick={() => { setEditUser(null); setUserForm({ name: "", email: "", password: "", role: "staff", phone: "" }); setShowUserModal(true); }} data-testid="add-user-btn">
              <Plus size={14} /> Add User
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Name</TableHead>
                  <TableHead className="font-semibold text-slate-700">Email</TableHead>
                  <TableHead className="font-semibold text-slate-700">Role</TableHead>
                  <TableHead className="font-semibold text-slate-700">Status</TableHead>
                  <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">{t("loading")}</TableCell></TableRow>
                ) : users.map(u => (
                  <TableRow key={u.id} className="table-row-hover" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-slate-900 text-sm">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <Badge className={`capitalize text-xs ${roleColors[u.role] || "bg-slate-100 text-slate-700"}`}>{u.role?.replace("_", " ")}</Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={u.is_active !== false} onCheckedChange={() => handleToggleUser(u)} data-testid={`toggle-user-${u.id}`} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-slate-900" onClick={() => { setEditUser(u); setUserForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "" }); setShowUserModal(true); }} data-testid={`edit-user-${u.id}`}>Edit</Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 size={13} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="fields" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-600">Dynamic fields allow you to add custom information to any entity without code changes.</p>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" size="sm" onClick={() => setShowFieldModal(true)} data-testid="add-field-btn">
              <Plus size={14} /> Add Field
            </Button>
          </div>
          {fields.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Layers size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-slate-500 text-sm">No custom fields yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Field Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Entity</TableHead>
                    <TableHead className="font-semibold text-slate-700">Type</TableHead>
                    <TableHead className="font-semibold text-slate-700">Required</TableHead>
                    <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map(f => (
                    <TableRow key={f.id} data-testid={`field-row-${f.id}`}>
                      <TableCell className="font-medium text-slate-900 text-sm">{f.field_name}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize text-xs">{f.entity}</Badge></TableCell>
                      <TableCell><Badge className="bg-slate-100 text-slate-700 text-xs capitalize">{f.field_type}</Badge></TableCell>
                      <TableCell>{f.required ? <Badge className="bg-rose-100 text-rose-700 text-xs">Required</Badge> : <span className="text-xs text-slate-400">Optional</span>}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDeleteField(f.id)} data-testid={`delete-field-${f.id}`}><Trash2 size={13} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Roles Info Tab */}
        <TabsContent value="roles" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { role: "admin", desc: "Full system access. Manage all modules, users, and settings.", perms: ["All modules", "User management", "Settings", "Reports"] },
              { role: "doctor", desc: "Access to appointments, patient records, and lab reports.", perms: ["Appointments", "Patients", "Lab reports", "Prescriptions"] },
              { role: "receptionist", desc: "Front-desk operations. Manage appointments and patient check-in.", perms: ["Appointments", "Patient registration", "Queue management"] },
              { role: "lab_technician", desc: "Lab module access for test management and results.", perms: ["Lab tests", "Patient lookup", "Results upload"] },
              { role: "staff", desc: "Basic access to the dashboard.", perms: ["Dashboard"] },
              { role: "patient", desc: "View personal appointments and billing.", perms: ["My appointments", "My bills"] },
            ].map(({ role, desc, perms }) => (
              <div key={role} className="bg-white rounded-xl border border-slate-200 p-4">
                <Badge className={`capitalize mb-2 ${roleColors[role]}`}>{role.replace("_", " ")}</Badge>
                <p className="text-xs text-slate-600 mb-3">{desc}</p>
                <ul className="space-y-1">
                  {perms.map(p => (
                    <li key={p} className="text-xs text-slate-500 flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">{editUser ? "Edit User" : "Create User"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveUser} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Full Name</Label>
                <Input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} className="mt-1 border-slate-200" required data-testid="user-name-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} className="mt-1 border-slate-200" required data-testid="user-email-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Phone</Label>
                <Input value={userForm.phone} onChange={e => setUserForm({...userForm, phone: e.target.value})} className="mt-1 border-slate-200" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Role</Label>
                <select value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize" data-testid="user-role-select">
                  {ROLES.map(r => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                </select>
              </div>
              {!editUser && (
                <div>
                  <Label className="text-sm text-slate-700">Password</Label>
                  <Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} className="mt-1 border-slate-200" required data-testid="user-pass-input" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-user-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Field Modal */}
      <Dialog open={showFieldModal} onOpenChange={setShowFieldModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">Add Custom Field</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveField} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Entity</Label>
              <select value={fieldForm.entity} onChange={e => setFieldForm({...fieldForm, entity: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize" data-testid="field-entity-select">
                {ENTITIES.map(e => <option key={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Field Name</Label>
              <Input value={fieldForm.field_name} onChange={e => setFieldForm({...fieldForm, field_name: e.target.value})} placeholder="e.g. Specialization Certificate No." className="mt-1 border-slate-200" required data-testid="field-name-input" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Field Type</Label>
              <select value={fieldForm.field_type} onChange={e => setFieldForm({...fieldForm, field_type: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize">
                {FIELD_TYPES.map(ft => <option key={ft}>{ft}</option>)}
              </select>
            </div>
            {fieldForm.field_type === "select" && (
              <div>
                <Label className="text-sm text-slate-700">Options (comma-separated)</Label>
                <Input value={fieldForm.options} onChange={e => setFieldForm({...fieldForm, options: e.target.value})} placeholder="Option1, Option2, Option3" className="mt-1 border-slate-200" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <input type="checkbox" id="field-required" checked={fieldForm.required} onChange={e => setFieldForm({...fieldForm, required: e.target.checked})} />
              <Label htmlFor="field-required" className="text-sm text-slate-700 cursor-pointer">Required field</Label>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowFieldModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-field-btn">
                {saving ? "Saving..." : "Add Field"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
